import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchAvatarRender } from './AvatarAPI';
import { AVATAR_PROFILES } from './avatarModel';
import './Avatar.css';

function currentDevicePixelRatio() {
  return typeof window === 'undefined' ? 1 : window.devicePixelRatio;
}

export default function AvatarComposite({
  selection,
  renderProfile = AVATAR_PROFILES.PORTRAIT,
  className = '',
  label = 'Customized survivor avatar',
  decorative = false,
  devicePixelRatio = currentDevicePixelRatio(),
  includeDrafts = false,
  creatorCard = false,
  focus = 'full',
  style,
}) {
  const [url, setUrl] = useState(null);
  const [renderState, setRenderState] = useState('loading');
  const urlRef = useRef(null);
  const request = useMemo(() => ({
    selection: selection || {},
    profile: renderProfile,
    focus,
    scale: devicePixelRatio >= 1.5 ? 2 : 1,
    creatorCard,
    includeDrafts,
  }), [
    selection,
    renderProfile,
    focus,
    devicePixelRatio,
    creatorCard,
    includeDrafts,
  ]);
  const requestKey = JSON.stringify(request);

  useEffect(() => {
    let cancelled = false;
    setRenderState('loading');
    fetchAvatarRender(request)
      .then(nextUrl => {
        if (cancelled) {
          URL.revokeObjectURL?.(nextUrl);
          return;
        }
        if (urlRef.current) URL.revokeObjectURL?.(urlRef.current);
        urlRef.current = nextUrl;
        setUrl(nextUrl);
        setRenderState('ready');
      })
      .catch(() => {
        if (!cancelled) setRenderState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL?.(urlRef.current);
  }, []);

  return (
    <div
      aria-hidden={decorative ? 'true' : undefined}
      aria-busy={renderState === 'loading'}
      aria-label={decorative
        ? undefined
        : renderState === 'loading'
          ? `Rendering ${label}`
          : renderState === 'error'
            ? `${label} unavailable`
            : label}
      className={`avatar-composite avatar-composite--${renderProfile} ${className}`.trim()}
      data-render-state={renderState}
      role={decorative ? undefined : renderState === 'loading' ? 'status' : 'img'}
      style={style}
    >
      {url && (
        <img
          alt=""
          aria-hidden="true"
          className="avatar-composite__image"
          decoding="async"
          draggable="false"
          src={url}
        />
      )}
      {renderState === 'loading' && (
        <span aria-hidden="true" className="avatar-composite__loading">
          <span aria-hidden="true" className="avatar-composite__spinner" />
        </span>
      )}
    </div>
  );
}
